(function () {
  'use strict';

  const DATA_SCRIPT_ID = 'travel-search-wetu-bridge-data';
  const WRAPPER_SELECTOR = '.travel-search-main-wrapper';
  const CARD_SELECTOR = '.accommodation-card';
  const BUTTON_SELECTOR = '.accommodation-btn';

  const wrapperState = new WeakMap();
  const initializedWrappers = new WeakSet();
  const instanceState = new WeakMap();
  const cardImagePoolState = new WeakMap();
  const globalAssignmentsBySourceId = new Map();
  const assignmentsByVariantId = new Map();
  let lastClickedAssignment = null;
  const galleryOverlayState = {
    root: null,
    track: null,
    closeButton: null,
  };

  function hasText(value) {
    return String(value ?? '').trim().length > 0;
  }

  function isShopifyCdnUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return false;
    try {
      const host = new URL(raw).hostname.toLowerCase();
      return host === 'cdn.shopify.com' || host === 'cdn.shopifycdn.net' || host === 'shopifycdn.com' || host.endsWith('.shopifycdn.com');
    } catch (_) {
      return false;
    }
  }

  function normalizeMatchKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function normalizeSourceId(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getAccommodationMatchPayload(accommodation) {
    const sourceId = normalizeSourceId(
      accommodation?.source_id || accommodation?.accommodation_id || accommodation?.accommodationId
    );
    const handleKey = normalizeMatchKey(accommodation?.handle || accommodation?.accommodation_handle);
    const nameKey = normalizeMatchKey(
      accommodation?.name || accommodation?.accommodation_name || accommodation?.room_name || accommodation?.title
    );
    const propertyKey = normalizeMatchKey(
      accommodation?.context_property_handle
      || accommodation?.property_handle
      || accommodation?.context_property_name
      || accommodation?.property_name
      || accommodation?.property_title
      || accommodation?.property
    );

    return {
      sourceId,
      handleKey,
      nameKey,
      propertyKey,
      propertyAndNameKey: propertyKey && nameKey ? `${propertyKey}::${nameKey}` : '',
    };
  }

  function normalizeVariantId(value) {
    if (!hasText(value)) return '';

    const normalized = String(value).trim();
    const gidMatch = normalized.match(/\/ProductVariant\/(\d+)(?:\D.*)?$/i);
    if (gidMatch && gidMatch[1]) return gidMatch[1];

    const trailingDigitsMatch = normalized.match(/(\d+)(?!.*\d)/);
    return trailingDigitsMatch && trailingDigitsMatch[1] ? trailingDigitsMatch[1] : normalized;
  }

  function registerAssignment(assignment) {
    const variantId = String(assignment?.variant_id || assignment?.variantId || '').trim();
    if (!variantId) return;

    assignmentsByVariantId.set(variantId, assignment);

    const normalizedVariantId = normalizeVariantId(variantId);
    if (normalizedVariantId && normalizedVariantId !== variantId) {
      assignmentsByVariantId.set(normalizedVariantId, assignment);
    }
  }

  function findAssignmentByVariantId(variantId) {
    if (!hasText(variantId)) return null;

    const rawValue = String(variantId).trim();
    if (assignmentsByVariantId.has(rawValue)) {
      return assignmentsByVariantId.get(rawValue);
    }

    const normalizedVariantId = normalizeVariantId(rawValue);
    if (normalizedVariantId && assignmentsByVariantId.has(normalizedVariantId)) {
      return assignmentsByVariantId.get(normalizedVariantId);
    }

    return null;
  }

  function cleanPropertyLabel(value) {
    const rawValue = String(value || '').trim();
    if (!rawValue) return '';

    const withoutPrefix = rawValue.replace(/^at\s+/i, '').trim();
    return withoutPrefix;
  }

  function getTextContent(element) {
    if (!(element instanceof Element)) return '';
    return String(element.textContent || '').trim();
  }

  function inferPropertyNameFromCard(card) {
    if (!(card instanceof Element)) return '';

    const dataCandidates = [
      card.dataset.wetuBridgePropertyName,
      card.dataset.propertyName,
      card.dataset.wetuPropertyName,
      card.dataset.propertyTitle,
    ];

    for (const candidate of dataCandidates) {
      const propertyName = cleanPropertyLabel(candidate);
      if (propertyName) return propertyName;
    }

    const selectorCandidates = [
      '.accommodation-property-link',
      '.accommodation-property-context a',
      '.accommodation-property-context',
      '[data-property-name]',
      '.accommodation-location',
    ];

    for (const selector of selectorCandidates) {
      const element = card.querySelector(selector);
      const propertyName = cleanPropertyLabel(getTextContent(element));
      if (propertyName) return propertyName;
    }

    return '';
  }

  function inferAccommodationNameFromCard(card) {
    if (!(card instanceof Element)) return '';

    const dataCandidates = [
      card.dataset.wetuBridgeName,
      card.dataset.accommodationName,
      card.dataset.roomName,
    ];

    for (const candidate of dataCandidates) {
      const accommodationName = String(candidate || '').trim();
      if (accommodationName) return accommodationName;
    }

    const selectorCandidates = ['.accommodation-name-link', '.accommodation-name', '[data-accommodation-name]'];
    for (const selector of selectorCandidates) {
      const element = card.querySelector(selector);
      const accommodationName = getTextContent(element);
      if (accommodationName) return accommodationName;
    }

    return '';
  }

  function rememberCardAssignment(card) {
    if (!(card instanceof Element)) return null;

    const variantId = String(card.dataset.wetuBridgeVariantId || card.dataset.variantId || '').trim();
    const title = inferAccommodationNameFromCard(card);
    const propertyTitle = inferPropertyNameFromCard(card);

    if (!title && !propertyTitle) return null;

    const assignment = {
      variant_id: variantId,
      title,
      property_title: propertyTitle,
    };

    if (variantId) {
      registerAssignment(assignment);
    }

    lastClickedAssignment = assignment;
    return assignment;
  }

  function findAssignmentByPayload(payload) {
    if (!payload || typeof payload !== 'object') return null;

    const sourceId = payload.source_id || payload.sourceId || payload.accommodation_id || payload.accommodationId;
    if (hasText(sourceId) && globalAssignmentsBySourceId.has(String(sourceId))) {
      return globalAssignmentsBySourceId.get(String(sourceId));
    }

    const variantCandidates = [payload.variant_id, payload.variantId, payload.merchandiseId, payload.id];
    for (const variantCandidate of variantCandidates) {
      const assignment = findAssignmentByVariantId(variantCandidate);
      if (assignment) return assignment;
    }

    if (Array.isArray(payload.items)) {
      for (const item of payload.items) {
        const assignment = findAssignmentByPayload(item);
        if (assignment) return assignment;
      }
    }

    if (payload.item && typeof payload.item === 'object') {
      return findAssignmentByPayload(payload.item);
    }

    return null;
  }

  function getBestAvailableAssignment(variantId, options) {
    const resolvedOptions = options && typeof options === 'object' ? options : {};
    const allowLastClicked = resolvedOptions.allowLastClicked === true;

    const assignmentFromVariant = findAssignmentByVariantId(variantId);
    if (assignmentFromVariant) return assignmentFromVariant;

    if (allowLastClicked && lastClickedAssignment && (hasText(lastClickedAssignment.property_title) || hasText(lastClickedAssignment.title))) {
      return lastClickedAssignment;
    }

    return null;
  }

  function hasReservationMarkersInPayload(payload) {
    if (!payload || typeof payload !== 'object') return false;

    const directKeys = ['reservation_id', '_reservation_id', 'check_in', 'checkin', 'check_out', 'checkout', 'nights', 'guests', 'rooms'];
    if (directKeys.some((key) => Object.prototype.hasOwnProperty.call(payload, key))) {
      return true;
    }

    const properties = payload.properties;
    if (properties && typeof properties === 'object' && !Array.isArray(properties)) {
      const propertyKeys = ['_reservation_id', 'Reservation ID', 'Check-in', 'Check-out', 'Nights', 'Guests', 'Rooms'];
      if (propertyKeys.some((key) => Object.prototype.hasOwnProperty.call(properties, key))) {
        return true;
      }
    }

    if (Array.isArray(payload.items)) {
      return payload.items.some((item) => hasReservationMarkersInPayload(item));
    }

    return false;
  }

  function hasReservationMarkersInParams(params) {
    if (!(params instanceof URLSearchParams)) return false;

    const markerPattern = /(reservation|check-?in|check-?out|nights|guests|rooms)/i;
    for (const [key, value] of params.entries()) {
      if (markerPattern.test(key) && hasText(value)) {
        return true;
      }
    }

    return false;
  }

  function setValueIfMissing(container, key, value) {
    if (!container || !hasText(value)) return false;
    if (hasText(container[key])) return false;

    container[key] = value;
    return true;
  }

  function applyCheckoutLabels(target, assignment) {
    if (!target || typeof target !== 'object' || !assignment) return false;

    const accommodationName = String(assignment.title || '').trim();
    const propertyName = String(assignment.property_title || '').trim();
    const accommodationDisplayName =
      accommodationName && propertyName
        ? `${accommodationName} at ${propertyName}`
        : accommodationName;
    if (!accommodationName && !propertyName) return false;

    let changed = false;

    if (!target.properties || Array.isArray(target.properties) || typeof target.properties !== 'object') {
      target.properties = {};
      changed = true;
    }

    if (propertyName) {
      changed = setValueIfMissing(target.properties, 'Property', propertyName) || changed;
      changed = setValueIfMissing(target.properties, '_property_name', propertyName) || changed;

      changed = setValueIfMissing(target, 'property_name', propertyName) || changed;
      changed = setValueIfMissing(target, 'propertyName', propertyName) || changed;
      changed = setValueIfMissing(target, 'property_title', propertyName) || changed;
      changed = setValueIfMissing(target, 'wetu_property_name', propertyName) || changed;
    }

    if (accommodationName) {
      changed = setValueIfMissing(target.properties, 'Accommodation', accommodationDisplayName || accommodationName) || changed;
      changed = setValueIfMissing(target.properties, '_accommodation_name', accommodationName) || changed;

      changed = setValueIfMissing(target, 'accommodation_name', accommodationDisplayName || accommodationName) || changed;
      changed = setValueIfMissing(target, 'accommodationName', accommodationName) || changed;
      changed = setValueIfMissing(target, 'room_name', accommodationName) || changed;
    }

    return changed;
  }

  function decorateCartPayload(payload, options) {
    if (!payload || typeof payload !== 'object') return false;

    const resolvedOptions = options && typeof options === 'object' ? options : {};
    const allowLastClicked = resolvedOptions.allowLastClicked === true;

    let changed = false;
    const fallbackAssignment =
      findAssignmentByPayload(payload) ||
      getBestAvailableAssignment(payload.variant_id || payload.variantId || payload.id, { allowLastClicked });

    if (Array.isArray(payload.items) && payload.items.length) {
      payload.items.forEach((item) => {
        const assignment =
          findAssignmentByPayload(item) ||
          getBestAvailableAssignment(item.variant_id || item.variantId || item.id || item.merchandiseId, { allowLastClicked }) ||
          fallbackAssignment;
        if (assignment && applyCheckoutLabels(item, assignment)) {
          changed = true;
        }
      });
      return changed;
    }

    if (fallbackAssignment && applyCheckoutLabels(payload, fallbackAssignment)) {
      changed = true;
    }

    return changed;
  }

  function findVariantIdInParams(params) {
    const directKeys = ['variant_id', 'variantId', 'id', 'merchandiseId'];

    for (const directKey of directKeys) {
      const candidate = params.get(directKey);
      if (hasText(candidate)) {
        return candidate;
      }
    }

    for (const [key, value] of params.entries()) {
      if (!hasText(value)) continue;
      if (/\[(?:variant_id|variantId|id|merchandiseId)\]$/i.test(key)) {
        return value;
      }
    }

    return '';
  }

  function setParamIfMissing(params, key, value) {
    if (!hasText(value)) return false;

    const existingValue = params.get(key);
    if (hasText(existingValue)) return false;

    params.set(key, value);
    return true;
  }

  function decorateParamsPayload(params, assignment) {
    if (!(params instanceof URLSearchParams) || !assignment) return false;

    const propertyName = String(assignment.property_title || '').trim();
    const accommodationName = String(assignment.title || '').trim();
    const accommodationDisplayName =
      accommodationName && propertyName
        ? `${accommodationName} at ${propertyName}`
        : accommodationName;

    let changed = false;

    changed = setParamIfMissing(params, 'property_name', propertyName) || changed;
    changed = setParamIfMissing(params, 'wetu_property_name', propertyName) || changed;
    changed = setParamIfMissing(params, 'accommodation_name', accommodationDisplayName || accommodationName) || changed;

    changed = setParamIfMissing(params, 'properties[Property]', propertyName) || changed;
    changed = setParamIfMissing(params, 'properties[_property_name]', propertyName) || changed;
    changed = setParamIfMissing(params, 'properties[Accommodation]', accommodationDisplayName || accommodationName) || changed;
    changed = setParamIfMissing(params, 'properties[_accommodation_name]', accommodationName) || changed;

    const itemPrefixes = new Set();
    for (const [key] of params.entries()) {
      const match = key.match(/^(items\[\d+\])\[(?:variant_id|variantId|id|merchandiseId)\]$/i);
      if (match && match[1]) {
        itemPrefixes.add(match[1]);
      }
    }

    itemPrefixes.forEach((itemPrefix) => {
      changed = setParamIfMissing(params, `${itemPrefix}[property_name]`, propertyName) || changed;
      changed = setParamIfMissing(params, `${itemPrefix}[wetu_property_name]`, propertyName) || changed;
      changed = setParamIfMissing(params, `${itemPrefix}[accommodation_name]`, accommodationDisplayName || accommodationName) || changed;

      changed = setParamIfMissing(params, `${itemPrefix}[properties][Property]`, propertyName) || changed;
      changed = setParamIfMissing(params, `${itemPrefix}[properties][_property_name]`, propertyName) || changed;
      changed = setParamIfMissing(params, `${itemPrefix}[properties][Accommodation]`, accommodationDisplayName || accommodationName) || changed;
      changed = setParamIfMissing(params, `${itemPrefix}[properties][_accommodation_name]`, accommodationName) || changed;
    });

    return changed;
  }

  function isCartAddEndpoint(url) {
    if (!hasText(url)) return false;

    const normalizedUrl = String(url).toLowerCase();
    return (
      /\/cart\/add(?:\.js)?(?:[/?#]|$)/.test(normalizedUrl) ||
      /\/apps\/plekify\/api\/cart\/add(?:[/?#]|$)/.test(normalizedUrl) ||
      /\/api\/cart\/add(?:[/?#]|$)/.test(normalizedUrl)
    );
  }

  function isPlekifyCartAddEndpoint(url) {
    if (!hasText(url)) return false;
    const normalizedUrl = String(url).toLowerCase();
    return /\/apps\/plekify\/api\/cart\/add(?:[/?#]|$)/.test(normalizedUrl) || /\/api\/cart\/add(?:[/?#]|$)/.test(normalizedUrl);
  }

  function installCartPayloadBridge() {
    if (window.__wetuBridgeCartPayloadPatched) return;
    if (typeof window.fetch !== 'function') return;

    const originalFetch = window.fetch.bind(window);

    window.fetch = function patchedFetch(input, init) {
      const requestUrl =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : typeof Request !== 'undefined' && input instanceof Request
              ? input.url
              : String(input || '');

      if (!isCartAddEndpoint(requestUrl) || !init || typeof init !== 'object' || !Object.prototype.hasOwnProperty.call(init, 'body')) {
        return originalFetch(input, init);
      }

      const nextInit = { ...init };
      const body = nextInit.body;
      const allowLastClickedFromUrl = isPlekifyCartAddEndpoint(requestUrl);

      try {
        if (typeof body === 'string') {
          try {
            const payload = JSON.parse(body);
            const allowLastClicked = allowLastClickedFromUrl || hasReservationMarkersInPayload(payload);
            if (decorateCartPayload(payload, { allowLastClicked })) {
              nextInit.body = JSON.stringify(payload);
            }
          } catch (_jsonParseError) {
            const params = new URLSearchParams(body);
            const allowLastClicked = allowLastClickedFromUrl || hasReservationMarkersInParams(params);
            const assignment = getBestAvailableAssignment(findVariantIdInParams(params), { allowLastClicked });

            if (assignment && decorateParamsPayload(params, assignment)) {
              nextInit.body = params.toString();
            }
          }
        } else if (body instanceof URLSearchParams) {
          const params = new URLSearchParams(body.toString());
          const allowLastClicked = allowLastClickedFromUrl || hasReservationMarkersInParams(params);
          const assignment = getBestAvailableAssignment(findVariantIdInParams(params), { allowLastClicked });

          if (assignment && decorateParamsPayload(params, assignment)) {
            nextInit.body = params;
          }
        } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
          const params = new URLSearchParams();
          body.forEach((value, key) => {
            if (typeof value === 'string') {
              params.append(key, value);
            }
          });

          const allowLastClicked = allowLastClickedFromUrl || hasReservationMarkersInParams(params);
          const assignment = getBestAvailableAssignment(findVariantIdInParams(params), { allowLastClicked });
          if (assignment && decorateParamsPayload(params, assignment)) {
            const nextFormData = new FormData();
            params.forEach((value, key) => {
              nextFormData.append(key, value);
            });
            nextInit.body = nextFormData;
          }
        }
      } catch (error) {
        console.warn('[WetuBridge] Unable to enrich cart payload with property context', error);
      }

      return originalFetch(input, nextInit);
    };

    window.__wetuBridgeCartPayloadPatched = true;
  }

  function normalizeImageList(candidates) {
    if (!Array.isArray(candidates)) return [];
    return uniqueImageSources(candidates);
  }

  function buildGalleryPools(item) {
    const accommodationImages = normalizeImageList([
      ...(Array.isArray(item?.accommodation_images) ? item.accommodation_images : []),
      item?.image_url,
      item?.secondary_image_url,
      item?.tertiary_image_url,
    ]);

    const propertyImages = normalizeImageList([
      ...(Array.isArray(item?.property_images) ? item.property_images : []),
      item?.property_image_url,
    ]);

    return {
      accommodation_images: accommodationImages,
      property_images: propertyImages,
    };
  }

  function parseBridgeData() {
    const script = document.getElementById(DATA_SCRIPT_ID);
    if (!script) return null;

    try {
      const raw = JSON.parse(script.textContent || '{}');
      const accommodations = Array.isArray(raw.accommodations) ? raw.accommodations : [];
      const properties = Array.isArray(raw.properties) ? raw.properties : [];

      const normalizedAccommodations = accommodations
        .filter((item) => item && item.title && item.image_url && item.variant_id)
        .map((item) => {
          const matchPayload = getAccommodationMatchPayload(item);
          const nextItem = {
            ...item,
            ...buildGalleryPools(item),
            source_id: matchPayload.sourceId,
            _source_id: matchPayload.sourceId,
            _handle_key: matchPayload.handleKey,
            _name_key: matchPayload.nameKey,
            _property_key: matchPayload.propertyKey,
            _property_and_name_key: matchPayload.propertyAndNameKey,
          };

          if (nextItem._source_id) {
            globalAssignmentsBySourceId.set(nextItem._source_id, nextItem);
          }

          registerAssignment(nextItem);
          return nextItem;
        });

      const normalizedProperties = properties
        .filter((item) => item && item.title && item.url)
        .map((item) => {
          return {
            ...item,
            gallery_images: normalizeImageList([...(Array.isArray(item.gallery_images) ? item.gallery_images : []), item.image_url]),
          };
        });

      return {
        accommodations: normalizedAccommodations,
        properties: normalizedProperties,
      };
    } catch (error) {
      console.error('[WetuBridge] Failed to parse bridge data', error);
      return null;
    }
  }

  function shuffle(items) {
    for (let i = items.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }

  function buildSpreadQueue(accommodations, properties) {
    if (!accommodations.length) return [];

    const propertyFallbacks = properties.length ? shuffle([...properties]) : [];
    let propertyFallbackIndex = 0;

    const enriched = accommodations.map((item, index) => {
      const nextItem = { ...item };

      if (!nextItem.property_url && propertyFallbacks.length) {
        const fallbackProperty = propertyFallbacks[propertyFallbackIndex % propertyFallbacks.length];
        propertyFallbackIndex += 1;

        nextItem.property_title = fallbackProperty.title;
        nextItem.property_handle = fallbackProperty.handle;
        nextItem.property_url = fallbackProperty.url;

        if (!nextItem.property_images?.length) {
          nextItem.property_images = Array.isArray(fallbackProperty.gallery_images) ? [...fallbackProperty.gallery_images] : [];
        }
      }

      if (!nextItem.property_image_url && nextItem.property_images?.length) {
        nextItem.property_image_url = nextItem.property_images[0];
      }

      nextItem._group_key = nextItem.property_handle || nextItem.property_url || `ungrouped-${index}`;
      return nextItem;
    });

    const groups = new Map();
    enriched.forEach((item) => {
      if (!groups.has(item._group_key)) {
        groups.set(item._group_key, []);
      }
      groups.get(item._group_key).push(item);
    });

    const groupKeys = shuffle(Array.from(groups.keys()));
    groupKeys.forEach((key) => shuffle(groups.get(key)));

    const queue = [];
    let hasItems = true;

    while (hasItems) {
      hasItems = false;

      for (const key of groupKeys) {
        const group = groups.get(key);
        if (!group || !group.length) continue;
        queue.push(group.shift());
        hasItems = true;
      }
    }

    return queue;
  }

  function getTravelSearchInstances() {
    const registry = window.travelSearchInstances;

    if (registry instanceof Map) return Array.from(registry.values());
    if (Array.isArray(registry)) return registry;
    if (registry && typeof registry === 'object' && registry.state) return [registry];

    return [];
  }

  function getOrCreateInstanceState(instance, bridgeData) {
    let state = instanceState.get(instance);
    if (state) return state;

    state = {
      bySourceId: new Map(),
      byHandleKey: new Map(),
      byNameKey: new Map(),
      byPropertyAndNameKey: new Map(),
      fallbackQueue: [],
      fallbackCursor: 0,
    };

    (Array.isArray(bridgeData?.accommodations) ? bridgeData.accommodations : []).forEach((assignment) => {
      const sourceId = normalizeSourceId(assignment?._source_id || assignment?.source_id);
      const handleKey = normalizeMatchKey(assignment?._handle_key || assignment?.handle);
      const nameKey = normalizeMatchKey(assignment?._name_key || assignment?.title);
      const propertyAndNameKey = String(assignment?._property_and_name_key || '').trim();

      if (sourceId && !state.bySourceId.has(sourceId)) {
        state.bySourceId.set(sourceId, assignment);
      }

      if (handleKey && !state.byHandleKey.has(handleKey)) {
        state.byHandleKey.set(handleKey, assignment);
      }

      if (nameKey) {
        if (!state.byNameKey.has(nameKey)) {
          state.byNameKey.set(nameKey, []);
        }
        state.byNameKey.get(nameKey).push(assignment);
      }

      if (propertyAndNameKey && !state.byPropertyAndNameKey.has(propertyAndNameKey)) {
        state.byPropertyAndNameKey.set(propertyAndNameKey, assignment);
      }
    });

    const allAccommodations = Array.isArray(bridgeData?.accommodations) ? bridgeData.accommodations : [];
    const withImages = allAccommodations.filter((a) => hasText(a?.image_url));
    state.fallbackQueue = withImages.length ? shuffle([...withImages]) : shuffle([...allAccommodations]);

    instanceState.set(instance, state);
    return state;
  }

  function getAssignmentForAccommodation(instance, bridgeData, accommodation) {
    if (!accommodation) return null;

    const state = getOrCreateInstanceState(instance, bridgeData);
    const matchPayload = getAccommodationMatchPayload(accommodation);
    let assignment = null;

    if (matchPayload.sourceId && state.bySourceId.has(matchPayload.sourceId)) {
      assignment = state.bySourceId.get(matchPayload.sourceId);
    }

    if (!assignment && matchPayload.propertyAndNameKey && state.byPropertyAndNameKey.has(matchPayload.propertyAndNameKey)) {
      assignment = state.byPropertyAndNameKey.get(matchPayload.propertyAndNameKey);
    }

    if (!assignment && matchPayload.handleKey && state.byHandleKey.has(matchPayload.handleKey)) {
      assignment = state.byHandleKey.get(matchPayload.handleKey);
    }

    if (!assignment && matchPayload.nameKey && state.byNameKey.has(matchPayload.nameKey)) {
      const nameMatches = state.byNameKey.get(matchPayload.nameKey);
      if (Array.isArray(nameMatches) && nameMatches.length === 1) {
        assignment = nameMatches[0];
      } else if (Array.isArray(nameMatches) && nameMatches.length > 1 && matchPayload.propertyKey) {
        assignment = nameMatches.find((candidate) => normalizeMatchKey(candidate?._property_key) === matchPayload.propertyKey) || null;
      }
    }

    if (!assignment && state.fallbackQueue.length) {
      assignment = state.fallbackQueue[state.fallbackCursor % state.fallbackQueue.length];
      state.fallbackCursor += 1;
    }

    if (!assignment) {
      return null;
    }

    if (matchPayload.sourceId) {
      state.bySourceId.set(matchPayload.sourceId, assignment);
      globalAssignmentsBySourceId.set(matchPayload.sourceId, assignment);
    }

    registerAssignment(assignment);

    return assignment;
  }

  function decorateAccommodationsForRender(instance, bridgeData, accommodations) {
    if (!Array.isArray(accommodations) || !accommodations.length) return accommodations;

    return accommodations.map((accommodation) => {
      const assignment = getAssignmentForAccommodation(instance, bridgeData, accommodation);
      if (!assignment) return accommodation;

      return {
        ...accommodation,
        name: assignment.title || accommodation.name,
        image: assignment.image_url || accommodation.image,
        concise_location: assignment.location_display || accommodation.concise_location,
        handle: assignment.handle || accommodation.handle,
        variant_id: assignment.variant_id || accommodation.variant_id,
        accommodation_name: assignment.title || accommodation.accommodation_name || accommodation.name,
        accommodation: assignment.title || accommodation.accommodation || accommodation.name,
        room_name: assignment.title || accommodation.room_name || accommodation.name,
        wetu_property_name: assignment.property_title || accommodation.wetu_property_name || accommodation.property_name,
        property_name: assignment.property_title || accommodation.property_name,
        property: assignment.property_title || accommodation.property,
        property_title: assignment.property_title || accommodation.property_title,
        property_handle: assignment.property_handle || accommodation.property_handle,
        _wetu_bridge: assignment,
      };
    });
  }

  function patchTravelSearchPrototype(bridgeData) {
    const Component = window.TravelSearchComponent;
    if (!Component || !Component.prototype) return false;

    const proto = Component.prototype;
    if (proto.__wetuBridgePatched) return true;

    if (typeof proto.displayResults === 'function') {
      const originalDisplayResults = proto.displayResults;

      proto.displayResults = function patchedDisplayResults(accommodations) {
        let nextAccommodations = accommodations;

        try {
          nextAccommodations = decorateAccommodationsForRender(this, bridgeData, accommodations);
        } catch (error) {
          console.error('[WetuBridge] Failed to decorate render payload', error);
        }

        return originalDisplayResults.call(this, nextAccommodations);
      };
    }

    proto.__wetuBridgePatched = true;
    return true;
  }

  function patchResultPayloadFromCard(card) {
    const sourceId = card.dataset.accommodationId;
    if (!sourceId) return;

    const rememberedAssignment = rememberCardAssignment(card);

    const nextVariantId = card.dataset.wetuBridgeVariantId;
    const nextProductId = card.dataset.wetuBridgeProductId;
    const nextName = card.dataset.wetuBridgeName || rememberedAssignment?.title;
    const nextImage = card.dataset.wetuBridgeImage;
    const nextHandle = card.dataset.wetuBridgeHandle;
    const nextPropertyName = card.dataset.wetuBridgePropertyName || rememberedAssignment?.property_title;
    const nextPropertyHandle = card.dataset.wetuBridgePropertyHandle;

    if (!nextVariantId || !nextName) return;

    getTravelSearchInstances().forEach((instance) => {
      const results = instance?.state?.results;
      if (!Array.isArray(results) || !results.length) return;

      const result = results.find((item) => String(item?.source_id) === String(sourceId));
      if (!result) return;

      result.name = nextName;
      result.variant_id = nextVariantId;
      result.accommodation_name = nextName;
      result.accommodation = nextName;
      result.room_name = nextName;

      if (nextImage) result.image = nextImage;
      if (nextHandle) result.handle = nextHandle;
      if (nextProductId) result.id = nextProductId;

      if (nextPropertyName) {
        result.wetu_property_name = nextPropertyName;
        result.property_name = nextPropertyName;
        result.property = nextPropertyName;
        result.property_title = nextPropertyName;

        if (!result.properties || Array.isArray(result.properties) || typeof result.properties !== 'object') {
          result.properties = {};
        }

        result.properties.Property = nextPropertyName;
        if (!hasText(result.properties.Accommodation)) {
          const nextAccommodationDisplayName = `${nextName} at ${nextPropertyName}`;
          result.properties.Accommodation = nextAccommodationDisplayName;
        }
      }

      if (nextPropertyHandle) {
        result.property_handle = nextPropertyHandle;
        result.propertyHandle = nextPropertyHandle;
      }
    });
  }

  function setPropertyContext(titleSection, assignment) {
    let propertyContext = titleSection.querySelector('.accommodation-property-context');

    if (!propertyContext) {
      propertyContext = document.createElement('p');
      propertyContext.className = 'accommodation-property-context';
    }

    propertyContext.textContent = '';

    const propertyName = assignment.property_title || '';
    if (!propertyName) {
      if (propertyContext.parentNode) propertyContext.remove();
      return;
    }

    const lodgeName = cleanPropertyLabel(
      getTextContent(titleSection.querySelector('.accommodation-lodge-link')) ||
      getTextContent(titleSection.querySelector('.accommodation-lodge-name'))
    );

    if (lodgeName && normalizeMatchKey(lodgeName) === normalizeMatchKey(propertyName)) {
      if (propertyContext.parentNode) propertyContext.remove();
      return;
    }

    if (assignment.property_url) {
      const propertyLink = document.createElement('a');
      propertyLink.className = 'accommodation-property-link';
      propertyLink.href = assignment.property_url;
      propertyLink.textContent = propertyName;
      propertyContext.appendChild(propertyLink);
    } else {
      propertyContext.textContent = propertyName;
    }

    const locationElement = titleSection.querySelector('.accommodation-location');
    if (locationElement && locationElement.nextSibling) {
      titleSection.insertBefore(propertyContext, locationElement.nextSibling);
    } else {
      titleSection.appendChild(propertyContext);
    }
  }

  function setTagline(titleSection, assignment) {
    const unitTagline = titleSection.querySelector('.accommodation-unit-tagline');
    const tagline = [
      assignment?.tagline,
      assignment?.accommodation_tagline,
      assignment?.wetu_tagline,
      assignment?.property_tagline,
      unitTagline?.textContent,
    ]
      .map((value) => String(value || '').trim())
      .find((value) => value.length > 0) || '';

    let taglineElement = titleSection.querySelector('.wetu-bridge-tagline');

    if (!taglineElement && unitTagline) {
      taglineElement = unitTagline;
      taglineElement.classList.remove('accommodation-unit-tagline');
      taglineElement.classList.add('wetu-bridge-tagline');
    }

    if (!tagline) {
      titleSection.querySelectorAll('.wetu-bridge-tagline, .accommodation-unit-tagline').forEach((element) => {
        element.remove();
      });
      return;
    }

    if (!taglineElement) {
      taglineElement = document.createElement('p');
      taglineElement.className = 'wetu-bridge-tagline';
      titleSection.appendChild(taglineElement);
    }

    titleSection.querySelectorAll('.wetu-bridge-tagline, .accommodation-unit-tagline').forEach((element) => {
      if (element !== taglineElement) {
        element.remove();
      }
    });

    taglineElement.textContent = tagline;
  }

  function getInfoColumn(card) {
    return card?.querySelector('.accommodation-info-column') || card?.querySelector('.accommodation-overlay-content') || null;
  }

  function getMetaColumn(card) {
    return card?.querySelector('.accommodation-meta-column') || card?.querySelector('.accommodation-overlay-content') || null;
  }

  function toFiniteNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const normalized = String(value ?? '').replace(/[^0-9.,-]/g, '').trim();
    if (!normalized) return null;

    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');
    let numericString = normalized;

    if (lastComma > lastDot) {
      numericString = normalized.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > lastComma) {
      numericString = normalized.replace(/,/g, '');
    } else {
      numericString = normalized.replace(',', '.');
    }

    const parsed = Number.parseFloat(numericString);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function formatAmountLike(sourceText, numericValue) {
    if (!hasText(sourceText) || !Number.isFinite(numericValue)) return '';

    const source = String(sourceText).trim();
    const numericMatch = source.match(/-?[\d\s.,]+/);
    if (!numericMatch || typeof numericMatch.index !== 'number') return '';

    const prefix = source.slice(0, numericMatch.index).trimEnd();
    const suffix = source.slice(numericMatch.index + numericMatch[0].length).trimStart();
    const numberPart = numericMatch[0].trim();
    const usesCommaDecimal = /,\d{1,2}$/.test(numberPart) && !/\.\d{1,2}$/.test(numberPart);
    const decimalPlaces = /[.,]\d{1,2}$/.test(numberPart) ? 2 : 0;

    let formatted = numericValue.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });

    if (usesCommaDecimal) {
      formatted = formatted.replace(/,/g, ' ').replace(/\./g, ',');
    }

    let output = formatted;
    if (prefix) output = `${prefix} ${output}`;
    if (suffix) output = `${output} ${suffix}`;
    return output.trim();
  }

  function parseGuestConfig(configText) {
    const rawText = String(configText || '').trim();
    if (!rawText) return { adults: null, children: null, rooms: null, nights: null };

    const matchValue = function (pattern) {
      const match = rawText.match(pattern);
      return match ? Number.parseInt(match[1], 10) : null;
    };

    return {
      adults: matchValue(/(\d+)\s*adult/i),
      children: matchValue(/(\d+)\s*child/i),
      rooms: matchValue(/(\d+)\s*room/i),
      nights: matchValue(/(\d+)\s*night/i),
    };
  }

  function formatCompactCount(value) {
    const numericValue = toFiniteNumber(value);
    if (numericValue === null) return '';
    if (numericValue >= 1000000) return `${(numericValue / 1000000).toFixed(numericValue >= 10000000 ? 0 : 1)}M`;
    if (numericValue >= 1000) return `${(numericValue / 1000).toFixed(numericValue >= 10000 ? 0 : 1)}k`;
    return String(Math.round(numericValue));
  }

  function inferPriceContext(card) {
    const priceRoot = card?.querySelector('.accommodation-price');
    if (!priceRoot) {
      return {
        amountText: '',
        amountValue: null,
        configText: '',
        datesText: '',
      };
    }

    const amountText = String(priceRoot.querySelector('.accommodation-price-amount')?.textContent || '').trim();
    const configText = String(priceRoot.querySelector('.accommodation-price-config')?.textContent || '').trim();
    const datesText = String(priceRoot.querySelector('.accommodation-price-dates')?.textContent || '').trim();

    return {
      amountText,
      amountValue: toFiniteNumber(amountText),
      configText,
      datesText,
    };
  }

  function truncateCopy(value, maxLength) {
    const normalized = String(value || '').trim().replace(/\s+/g, ' ');
    if (!normalized || normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
  }

  function uniqueStrings(values) {
    const seen = new Set();
    return values
      .map((value) => String(value || '').trim())
      .filter((value) => {
        if (!value) return false;
        const key = value.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  function normalizeStringList(values) {
    if (Array.isArray(values)) return uniqueStrings(values);
    if (typeof values === 'string') {
      return uniqueStrings(
        values
          .split(/\s*[|,;]\s*/)
          .map((entry) => entry.trim())
          .filter(Boolean)
      );
    }
    return [];
  }

  function formatOccupancyLabel(assignment) {
    const maxAdults = toPositiveInt(assignment?.max_adults);
    const maxChildren = toPositiveInt(assignment?.max_children);
    const maxOccupancy = toPositiveInt(assignment?.max_occupancy);

    if (maxAdults && maxChildren !== null) {
      if (maxChildren > 0) return `${maxAdults} adults + ${maxChildren} children`;
      return `Up to ${maxAdults} adults`;
    }

    if (maxAdults) return `Up to ${maxAdults} guests`;
    if (maxOccupancy) return `Up to ${maxOccupancy} guests`;
    return '';
  }

  function buildPriceBreakdownLines(card, assignment) {
    const priceContext = inferPriceContext(card);
    const lines = [];
    const guestConfig = parseGuestConfig(priceContext.configText);

    if (priceContext.amountValue !== null && guestConfig.nights && guestConfig.nights > 0) {
      const nightlyAmount = priceContext.amountValue / guestConfig.nights;
      const nightlyDisplay = formatAmountLike(priceContext.amountText, nightlyAmount);

      if (nightlyDisplay) {
        lines.push(`Approx. ${nightlyDisplay} / night`);
      }
    }

    if (guestConfig.adults || guestConfig.children || guestConfig.rooms) {
      const pieces = [];
      if (guestConfig.adults) pieces.push(`${guestConfig.adults} adult${guestConfig.adults === 1 ? '' : 's'}`);
      if (guestConfig.children) pieces.push(`${guestConfig.children} child${guestConfig.children === 1 ? '' : 'ren'}`);
      if (guestConfig.rooms) pieces.push(`${guestConfig.rooms} room${guestConfig.rooms === 1 ? '' : 's'}`);
      if (pieces.length) lines.push(pieces.join(' · '));
    }

    const occupancyLabel = formatOccupancyLabel(assignment);
    if (occupancyLabel) lines.push(occupancyLabel);
    if (priceContext.datesText) lines.push(priceContext.datesText);

    return uniqueStrings(lines).slice(0, 3);
  }

  function formatPolicyChip(label) {
    return { label, kind: 'policy' };
  }

  function collectPolicyChips(assignment) {
    const combinedSignals = [
      ...normalizeStringList(assignment?.property_services),
      ...normalizeStringList(assignment?.property_facilities),
      ...normalizeStringList(assignment?.amenities),
      String(assignment?.property_child_policy || ''),
      String(assignment?.property_cancellation_policy || ''),
    ].join(' ').toLowerCase();

    const chips = [];
    const addChip = function (label) {
      if (!label) return;
      if (chips.some((chip) => chip.label === label)) return;
      chips.push(formatPolicyChip(label));
    };

    if (/vegan/.test(combinedSignals)) addChip('Vegan-friendly');
    if (/vegetarian/.test(combinedSignals)) addChip('Vegetarian options');
    if (/(gluten|coeliac|celiac)/.test(combinedSignals)) addChip('Gluten-aware');
    if (/(wheelchair|accessible|accessibility|step[- ]free|elevator|lift|mobility|ground floor)/.test(combinedSignals)) {
      addChip('Accessibility noted');
    }

    const childPolicy = String(assignment?.property_child_policy || '').trim();
    if (childPolicy) {
      if (/adult[-\s]?only|adults[-\s]?only|no children/i.test(childPolicy)) {
        addChip('Adults only');
      } else if (/child|children|family/i.test(childPolicy)) {
        addChip('Child policy');
      }
    }

    const cancellationPolicy = String(assignment?.property_cancellation_policy || '').trim();
    if (cancellationPolicy) {
      if (/free cancellation/i.test(cancellationPolicy)) {
        addChip('Free cancellation');
      } else if (/non[-\s]?refundable/i.test(cancellationPolicy)) {
        addChip('Non-refundable');
      } else {
        addChip('Cancellation terms');
      }
      if (/(deposit|prepay|pre-pay|full payment|balance due)/i.test(cancellationPolicy)) {
        addChip('Payment terms');
      }
    }

    return chips.slice(0, 6);
  }

  function collectSelectedHighlights(assignment) {
    return uniqueStrings([
      ...normalizeStringList(assignment?.property_highlights),
      ...normalizeStringList(assignment?.amenities),
      ...normalizeStringList(assignment?.property_facilities),
    ]).slice(0, 6);
  }

  function setTrustStrip(card, assignment) {
    const infoColumn = getInfoColumn(card);
    const titleSection = card.querySelector('.accommodation-title-section');
    if (!infoColumn || !titleSection) return;

    const items = [];
    const googleRating = toFiniteNumber(assignment?.google_rating);
    const reviewCount = toFiniteNumber(assignment?.google_review_count);

    if (googleRating !== null) {
      let ratingText = googleRating.toFixed(1);
      if (reviewCount !== null) {
        ratingText = `${ratingText} · ${formatCompactCount(reviewCount)} reviews`;
      }
      items.push(ratingText);
    }

    const starRating = toFiniteNumber(assignment?.property_star_rating);
    if (starRating !== null) {
      items.push(`${Math.round(starRating)}-star property`);
    }

    const category = String(assignment?.property_category || '').trim();
    if (category) items.push(category);

    let trustStrip = infoColumn.querySelector('.wetu-result-trust');
    if (!items.length) {
      if (trustStrip) trustStrip.remove();
      return;
    }

    if (!trustStrip) {
      trustStrip = document.createElement('div');
      trustStrip.className = 'wetu-result-trust';
      titleSection.insertAdjacentElement('afterend', trustStrip);
    }

    trustStrip.innerHTML = '';
    items.forEach((itemText, index) => {
      const item = document.createElement('span');
      item.className = 'wetu-result-trust__item';
      if (index === 0 && googleRating !== null) item.classList.add('wetu-result-trust__item--rating');
      item.textContent = itemText;
      trustStrip.appendChild(item);
    });
  }

  function setSummary(card, assignment) {
    const infoColumn = getInfoColumn(card);
    if (!infoColumn) return;

    const summaryText = String(assignment.summary || assignment.property_editorial_summary || '').trim();
    let summaryElement = infoColumn.querySelector('.wetu-bridge-summary');

    if (!summaryText) {
      if (summaryElement) summaryElement.remove();
      return;
    }

    if (!summaryElement) {
      summaryElement = document.createElement('p');
      summaryElement.className = 'wetu-bridge-summary';
      infoColumn.appendChild(summaryElement);
    }

    summaryElement.textContent = summaryText;
  }

  function toPositiveInt(value) {
    const numericValue = Number.parseInt(String(value ?? '').trim(), 10);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
  }

  function collectDisplayDetailRows(assignment) {
    const rows = [];
    const pushRow = (label, value) => {
      const normalized = String(value ?? '').trim();
      if (!normalized) return;
      rows.push({ label, value: normalized });
    };

    pushRow('Room', assignment?.room_type);
    pushRow('Sleeps', formatOccupancyLabel(assignment));
    pushRow('Meal plan', assignment?.room_basis);
    pushRow('Beds', assignment?.beds_summary);
    pushRow('View', assignment?.view_type);
    pushRow('Room size', assignment?.room_size);

    const checkIn = String(assignment?.property_check_in_time || '').trim();
    const checkOut = String(assignment?.property_check_out_time || '').trim();
    if (checkIn || checkOut) {
      const parts = [];
      if (checkIn) parts.push(`Check-in ${checkIn}`);
      if (checkOut) parts.push(`Check-out ${checkOut}`);
      rows.push({ label: null, value: parts.join(' · ') });
    }

    return rows;
  }

  function setCardLocation(card, assignment) {
    const locationElement = card.querySelector('.accommodation-location');
    if (!locationElement) return;

    let nextLocation = String(assignment?.location_display || '').trim();
    if (!nextLocation) {
      nextLocation = String(locationElement.textContent || '').trim();
    }

    if (!nextLocation) {
      locationElement.hidden = true;
      return;
    }

    const parts = nextLocation.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    const shortLocation = parts.length > 2 ? parts.slice(0, 2).join(', ') : nextLocation;

    locationElement.innerHTML = '';
    const pin = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    pin.setAttribute('width', '12');
    pin.setAttribute('height', '12');
    pin.setAttribute('viewBox', '0 0 24 24');
    pin.setAttribute('fill', 'none');
    pin.setAttribute('stroke', 'currentColor');
    pin.setAttribute('stroke-width', '2');
    pin.setAttribute('aria-hidden', 'true');
    pin.classList.add('wetu-pin-icon');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z');
    pin.appendChild(path);

    locationElement.appendChild(pin);
    locationElement.appendChild(document.createTextNode(' ' + shortLocation));
    locationElement.hidden = false;
  }

  function setRichDetails(card, assignment) {
    const infoColumn = getInfoColumn(card);
    if (!infoColumn) return;

    const detailRows = collectDisplayDetailRows(assignment);
    const amenities = normalizeStringList(assignment?.amenities).slice(0, 8);
    const highlights = collectSelectedHighlights(assignment);
    const policyChips = collectPolicyChips(assignment);

    let detailsRoot = infoColumn.querySelector('.wetu-bridge-rich-details');
    if (!detailRows.length && !amenities.length && !highlights.length && !policyChips.length) {
      if (detailsRoot) detailsRoot.remove();
      return;
    }

    if (!detailsRoot) {
      detailsRoot = document.createElement('div');
      detailsRoot.className = 'wetu-bridge-rich-details';
      infoColumn.appendChild(detailsRoot);
    }

    detailsRoot.innerHTML = '';

    if (highlights.length) {
      const highlightList = document.createElement('ul');
      highlightList.className = 'wetu-bridge-highlights';

      highlights.forEach((highlight) => {
        const highlightItem = document.createElement('li');
        highlightItem.className = 'wetu-bridge-highlights__item';
        highlightItem.textContent = highlight;
        highlightList.appendChild(highlightItem);
      });

      detailsRoot.appendChild(highlightList);
    }

    if (detailRows.length) {
      const detailList = document.createElement('dl');
      detailList.className = 'wetu-bridge-details-grid';

      detailRows.forEach((row) => {
        if (row.label === null) {
          const standalone = document.createElement('dd');
          standalone.className = 'wetu-bridge-detail-standalone';
          standalone.textContent = row.value;
          detailList.appendChild(standalone);
          return;
        }

        const label = document.createElement('dt');
        label.className = 'wetu-bridge-detail-label';
        label.textContent = row.label;

        const value = document.createElement('dd');
        value.className = 'wetu-bridge-detail-value';
        value.textContent = row.value;

        detailList.appendChild(label);
        detailList.appendChild(value);
      });

      detailsRoot.appendChild(detailList);
    }

    if (amenities.length) {
      const amenityList = document.createElement('ul');
      amenityList.className = 'wetu-bridge-amenities';

      amenities.slice(0, 8).forEach((amenity) => {
        const amenityItem = document.createElement('li');
        amenityItem.className = 'wetu-bridge-amenity-chip';
        amenityItem.textContent = amenity;
        amenityList.appendChild(amenityItem);
      });

      detailsRoot.appendChild(amenityList);
    }

    if (policyChips.length) {
      const policyList = document.createElement('ul');
      policyList.className = 'wetu-bridge-policy-chips';

      policyChips.forEach((chip) => {
        const policyItem = document.createElement('li');
        policyItem.className = 'wetu-bridge-policy-chip';
        policyItem.textContent = chip.label;
        policyList.appendChild(policyItem);
      });

      detailsRoot.appendChild(policyList);
    }
  }

  function setVerifiedFacts(card, assignment) {
    const infoColumn = getInfoColumn(card);
    if (!infoColumn) return;

    const facts = [];
    const googleRating = toFiniteNumber(assignment?.google_rating);
    const reviewCount = toFiniteNumber(assignment?.google_review_count);
    const priceContext = inferPriceContext(card);

    if (priceContext.amountText) {
      const configSuffix = priceContext.configText ? ` for ${priceContext.configText}` : '';
      facts.push(`Live PMS total: ${priceContext.amountText}${configSuffix}.`);
    }
    if (googleRating !== null) {
      const reviewSuffix = reviewCount !== null ? ` from ${formatCompactCount(reviewCount)} reviews` : '';
      facts.push(`Social proof: ${googleRating.toFixed(1)}${reviewSuffix}.`);
    }
    if (assignment?.property_check_in_time || assignment?.property_check_out_time) {
      const parts = [];
      if (assignment.property_check_in_time) parts.push(`Check-in ${assignment.property_check_in_time}`);
      if (assignment.property_check_out_time) parts.push(`Check-out ${assignment.property_check_out_time}`);
      facts.push(parts.join(' · '));
    }
    if (assignment?.property_child_policy) {
      facts.push(`Child policy: ${truncateCopy(assignment.property_child_policy, 120)}`);
    }
    if (assignment?.property_cancellation_policy) {
      facts.push(`Cancellation / payment: ${truncateCopy(assignment.property_cancellation_policy, 120)}`);
    }
    if (assignment?.property_url) {
      facts.push('Property micro-catalog available on the same domain.');
    }

    let factsRoot = infoColumn.querySelector('.wetu-verified-facts');
    if (!facts.length) {
      if (factsRoot) factsRoot.remove();
      return;
    }

    if (!factsRoot) {
      factsRoot = document.createElement('details');
      factsRoot.className = 'wetu-verified-facts';
      factsRoot.innerHTML = [
        '<summary class="wetu-verified-facts__summary">Why trust this result</summary>',
        '<div class="wetu-verified-facts__content">',
        '  <p class="wetu-verified-facts__eyebrow">Verified facts</p>',
        '  <ul class="wetu-verified-facts__list"></ul>',
        '</div>',
      ].join('');
      infoColumn.appendChild(factsRoot);
    }

    const factList = factsRoot.querySelector('.wetu-verified-facts__list');
    if (!factList) return;
    factList.innerHTML = '';

    facts.slice(0, 5).forEach((factText) => {
      const item = document.createElement('li');
      item.textContent = factText;
      factList.appendChild(item);
    });
  }

  function setPriceBreakdown(card, assignment) {
    const metaColumn = getMetaColumn(card);
    const priceRoot = card.querySelector('.accommodation-price');
    if (!metaColumn || !priceRoot) return;

    const lines = buildPriceBreakdownLines(card, assignment);
    let breakdown = metaColumn.querySelector('.wetu-price-breakdown');

    if (!lines.length) {
      if (breakdown) breakdown.remove();
      return;
    }

    if (!breakdown) {
      breakdown = document.createElement('div');
      breakdown.className = 'wetu-price-breakdown';
      priceRoot.insertAdjacentElement('afterend', breakdown);
    }

    breakdown.innerHTML = '';
    lines.forEach((lineText, index) => {
      const line = document.createElement('p');
      line.className = 'wetu-price-breakdown__line';
      if (index === 0) line.classList.add('wetu-price-breakdown__line--primary');
      line.textContent = lineText;
      breakdown.appendChild(line);
    });
  }

  function setMetaActions(card, assignment) {
    const actions = card.querySelector('.accommodation-actions');
    const bookNowButton = actions?.querySelector('.accommodation-btn-buy');
    if (!actions || !bookNowButton) return;

    bookNowButton.textContent = 'Book Now';

    // Remove any existing "View property" button - single CTA only per requirements
    const existingPropertyLink = actions.querySelector('.wetu-property-cta');
    if (existingPropertyLink) {
      existingPropertyLink.remove();
    }
  }

  function setImageCTA(card, assignment) {
    const imageContainer = card.querySelector('.accommodation-image-container');
    if (!imageContainer) return;

    // Remove any existing CTA
    const existingCTA = imageContainer.querySelector('.wetu-image-cta');
    if (existingCTA) existingCTA.remove();

    // Determine the best URL to link to (accommodation page or property page)
    const targetUrl = assignment?.url || assignment?.property_url;
    if (!targetUrl) return;

    const targetName = assignment?.title || assignment?.property_title || 'property';

    // Create hover CTA overlay
    const cta = document.createElement('a');
    cta.className = 'wetu-image-cta';
    cta.href = targetUrl;
    cta.target = '_blank';
    cta.rel = 'noopener';
    cta.setAttribute('aria-label', `View ${targetName} details`);

    const label = document.createElement('span');
    label.className = 'wetu-image-cta__label';
    label.innerHTML = 'View Details <span class="wetu-image-cta__arrow">→</span>';

    cta.appendChild(label);
    imageContainer.appendChild(cta);
  }

  function uniqueImageSources(sourceCandidates) {
    const seen = new Set();

    return sourceCandidates.filter((source) => {
      if (!isShopifyCdnUrl(source)) return false;
      const nextSource = String(source || '').trim();
      if (!nextSource || seen.has(nextSource)) return false;

      seen.add(nextSource);
      return true;
    });
  }

  function getAssignmentImageSources(assignment) {
    const accommodationImages = uniqueImageSources([
      ...(Array.isArray(assignment?.accommodation_images) ? assignment.accommodation_images : []),
      assignment?.image_url,
      assignment?.secondary_image_url,
      assignment?.tertiary_image_url,
    ]);

    const propertyImages = uniqueImageSources([
      ...(Array.isArray(assignment?.property_images) ? assignment.property_images : []),
      assignment?.property_image_url,
    ]);

    return {
      accommodationImages,
      propertyImages,
      allImages: uniqueImageSources([...accommodationImages, ...propertyImages]),
    };
  }

  function getCardImageSources(card) {
    const cachedImagePool = cardImagePoolState.get(card);
    if (Array.isArray(cachedImagePool) && cachedImagePool.length) {
      return cachedImagePool;
    }

    const primaryImage = card.querySelector('.accommodation-image.primary, .accommodation-image');
    const secondaryImage = card.querySelector('.accommodation-image.secondary');

    return uniqueImageSources([
      primaryImage?.currentSrc || primaryImage?.src,
      secondaryImage?.currentSrc || secondaryImage?.src,
      card.dataset.wetuBridgeSecondaryImage,
      card.dataset.wetuBridgeTertiaryImage,
      card.dataset.wetuBridgePropertyImage,
    ]);
  }

  function closeResultGallery() {
    if (!galleryOverlayState.root) return;

    galleryOverlayState.root.classList.remove('is-open');
    galleryOverlayState.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wetu-result-gallery-open');

    if (galleryOverlayState.track) {
      galleryOverlayState.track.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }

  function ensureGalleryOverlay() {
    if (galleryOverlayState.root && galleryOverlayState.track) {
      return galleryOverlayState;
    }

    const track = document.createElement('div');
    const overlay = document.createElement('div');
    const closeButton = document.createElement('button');

    overlay.className = 'wetu-result-gallery-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Accommodation and property gallery');

    track.className = 'wetu-result-gallery-track';

    closeButton.type = 'button';
    closeButton.className = 'wetu-result-gallery-close';
    closeButton.setAttribute('aria-label', 'Close gallery');
    closeButton.textContent = 'Close';
    closeButton.addEventListener('click', closeResultGallery);

    overlay.appendChild(closeButton);
    overlay.appendChild(track);
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        closeResultGallery();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeResultGallery();
      }
    });

    document.body.appendChild(overlay);
    galleryOverlayState.root = overlay;
    galleryOverlayState.track = track;
    galleryOverlayState.closeButton = closeButton;

    return galleryOverlayState;
  }

  function openResultGallery(card, nextIndex) {
    const imageSources = getCardImageSources(card);
    if (!imageSources.length) return;

    const overlay = ensureGalleryOverlay();
    const signature = imageSources.join('|');

    if (overlay.track.dataset.sources !== signature) {
      overlay.track.dataset.sources = signature;
      overlay.track.innerHTML = '';

      imageSources.forEach((source, index) => {
        const slide = document.createElement('figure');
        slide.className = 'wetu-result-gallery-slide';

        const image = document.createElement('img');
        image.src = source;
        image.alt = `${card.dataset.wetuBridgeName || 'Accommodation'} full screen image ${index + 1}`;
        image.loading = 'lazy';
        image.addEventListener('click', (event) => {
          event.preventDefault();
          closeResultGallery();
        });

        slide.appendChild(image);
        overlay.track.appendChild(slide);
      });
    }

    const safeIndex = Number.isInteger(nextIndex) && nextIndex >= 0 && nextIndex < imageSources.length ? nextIndex : 0;

    overlay.root.classList.add('is-open');
    overlay.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wetu-result-gallery-open');

    window.requestAnimationFrame(() => {
      const targetSlide = overlay.track.children[safeIndex];

      if (targetSlide instanceof HTMLElement) {
        targetSlide.scrollIntoView({ behavior: 'auto', block: 'start' });
      } else {
        overlay.track.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    });
  }

  function setActiveCardImage(card, imageSources, nextIndex) {
    if (!Array.isArray(imageSources) || !imageSources.length) return;

    const primaryImage = card.querySelector('.accommodation-image.primary, .accommodation-image');
    if (!primaryImage) return;

    const safeIndex = Number.isInteger(nextIndex) && nextIndex >= 0 && nextIndex < imageSources.length ? nextIndex : 0;
    const selectedImage = imageSources[safeIndex];

    if (selectedImage) {
      primaryImage.src = selectedImage;
      primaryImage.removeAttribute('srcset');
      primaryImage.classList.remove('error');
      primaryImage.classList.add('loaded');
    }

    card.dataset.wetuActiveImageIndex = String(safeIndex);
    card.querySelectorAll('.wetu-card-thumbnail').forEach((thumbnail, index) => {
      thumbnail.classList.toggle('is-active', index === safeIndex);
      thumbnail.setAttribute('aria-pressed', index === safeIndex ? 'true' : 'false');
    });
  }

  function buildCardThumbnailRail(card) {
    const imageContainer = card.querySelector('.accommodation-image-container');
    const primaryImage = card.querySelector('.accommodation-image.primary, .accommodation-image');
    if (!imageContainer || !primaryImage) return;

    const imageSources = getCardImageSources(card);

    if (!imageSources.length) return;

    let activeIndex = Number.parseInt(card.dataset.wetuActiveImageIndex || '0', 10);
    if (!Number.isInteger(activeIndex) || activeIndex < 0 || activeIndex >= imageSources.length) {
      activeIndex = 0;
    }

    const maxThumbCount = Math.min(imageSources.length, 4);
    if (activeIndex >= maxThumbCount) {
      activeIndex = 0;
    }

    if (imageSources.length < 2) {
      const existingRail = card.querySelector('.wetu-card-thumbnails');
      if (existingRail) existingRail.remove();
      setActiveCardImage(card, imageSources, activeIndex);
      return;
    }

    let thumbnailRail = card.querySelector('.wetu-card-thumbnails');
    if (!thumbnailRail) {
      thumbnailRail = document.createElement('div');
      thumbnailRail.className = 'wetu-card-thumbnails';
      imageContainer.appendChild(thumbnailRail);
    }

    const railSignature = imageSources.join('|');
    if (thumbnailRail.dataset.sources !== railSignature) {
      thumbnailRail.dataset.sources = railSignature;
      thumbnailRail.innerHTML = '';

      imageSources.slice(0, 4).forEach((source, index) => {
        const thumbnailButton = document.createElement('button');
        thumbnailButton.type = 'button';
        thumbnailButton.className = 'wetu-card-thumbnail';
        thumbnailButton.dataset.imageIndex = String(index);
        thumbnailButton.setAttribute('aria-label', `View image ${index + 1}`);

        const thumbnailImage = document.createElement('img');
        thumbnailImage.src = source;
        thumbnailImage.alt = `${card.dataset.wetuBridgeName || 'Accommodation'} photo ${index + 1}`;
        thumbnailImage.loading = 'lazy';
        thumbnailButton.appendChild(thumbnailImage);

        thumbnailButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          setActiveCardImage(card, imageSources, index);
          openResultGallery(card, index);
        });

        thumbnailRail.appendChild(thumbnailButton);
      });
    }

    setActiveCardImage(card, imageSources, activeIndex);

    if (!primaryImage.dataset.wetuGalleryBound) {
      primaryImage.dataset.wetuGalleryBound = 'true';
      primaryImage.addEventListener('click', (event) => {
        event.preventDefault();
        const currentIndex = Number.parseInt(card.dataset.wetuActiveImageIndex || '0', 10);
        const safeIndex = Number.isInteger(currentIndex) ? currentIndex : 0;
        openResultGallery(card, safeIndex);
      });
    }
  }

  function enhanceCardPresentation(card) {
    card.classList.add('wetu-card-enhanced');
  }

  function applyAssignmentToCard(card, assignment) {
    if (!assignment) return;

    registerAssignment(assignment);

    const imagePool = getAssignmentImageSources(assignment);
    const accommodationPrimaryImage = imagePool.accommodationImages[0] || assignment.image_url;
    const accommodationSecondaryImage = imagePool.accommodationImages[1] || assignment.secondary_image_url;
    const combinedGalleryImages = imagePool.allImages;

    cardImagePoolState.set(card, combinedGalleryImages);

    const primaryImage = card.querySelector('.accommodation-image.primary, .accommodation-image');
    if (primaryImage && accommodationPrimaryImage) {
      primaryImage.src = accommodationPrimaryImage;
      primaryImage.alt = assignment.title;
      primaryImage.classList.remove('error');
      primaryImage.classList.add('loaded');
      primaryImage.removeAttribute('srcset');

      // Handle image load errors - prevent gray backgrounds
      if (!primaryImage.dataset.errorHandlerBound) {
        primaryImage.dataset.errorHandlerBound = 'true';
        primaryImage.addEventListener('error', function handleImageError() {
          this.classList.add('error');
          this.classList.remove('loaded');
          // Try fallback to property image if available
          const fallbackUrl = card.dataset.wetuBridgePropertyImage;
          if (fallbackUrl && this.src !== fallbackUrl) {
            this.src = fallbackUrl;
          }
        });
      }
    }

    const secondaryImage = card.querySelector('.accommodation-image.secondary');
    if (secondaryImage && accommodationSecondaryImage) {
      secondaryImage.src = accommodationSecondaryImage;
      secondaryImage.alt = assignment.title;
      secondaryImage.removeAttribute('srcset');
    }

    const imageContainer = card.querySelector('.accommodation-image-container');
    if (imageContainer) {
      imageContainer.classList.remove('has-secondary', 'hover');
    }

    if (secondaryImage) {
      secondaryImage.remove();
    }

    const nameHeading = card.querySelector('.accommodation-name');
    let nameLink = card.querySelector('.accommodation-name-link');

    if (!nameLink && nameHeading) {
      nameLink = document.createElement('a');
      nameLink.className = 'accommodation-name-link';
      nameHeading.textContent = '';
      nameHeading.appendChild(nameLink);
    }

    if (nameLink) {
      nameLink.textContent = assignment.title;
      if (assignment.url) nameLink.href = assignment.url;
    } else if (nameHeading) {
      nameHeading.textContent = assignment.title;
    }

    const lodgeName = card.querySelector('.accommodation-lodge-name');
    if (lodgeName && assignment.property_title) {
      lodgeName.classList.add('wetu-bridge-brand-label');
      const lodgeLink = lodgeName.querySelector('.accommodation-lodge-link');
      if (lodgeLink) {
        lodgeLink.textContent = assignment.property_title;
        if (assignment.property_url) lodgeLink.href = assignment.property_url;
        lodgeLink.setAttribute('aria-label', `View ${assignment.property_title} property details`);
      } else {
        lodgeName.textContent = assignment.property_title;
      }
    }

    const titleSection = card.querySelector('.accommodation-title-section');
    if (titleSection) {
      try { setPropertyContext(titleSection, assignment); } catch (_) {}
      try { setTagline(titleSection, assignment); } catch (_) {}
    }

    try { setCardLocation(card, assignment); } catch (_) {}
    try { setTrustStrip(card, assignment); } catch (_) {}
    try { setSummary(card, assignment); } catch (_) {}
    try { setRichDetails(card, assignment); } catch (_) {}
    // setVerifiedFacts removed - "Why trust this result" hidden per requirements
    try { setPriceBreakdown(card, assignment); } catch (_) {}
    try { setMetaActions(card, assignment); } catch (_) {}
    try { setImageCTA(card, assignment); } catch (_) {}

    if (assignment.variant_id) {
      card.dataset.variantId = assignment.variant_id;
      card.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
        button.dataset.variantId = assignment.variant_id;
      });
    }

    card.dataset.wetuBridgeName = assignment.title || '';
    card.dataset.wetuBridgeHandle = assignment.handle || '';
    card.dataset.wetuBridgeImage = accommodationPrimaryImage || '';
    card.dataset.wetuBridgeSecondaryImage = accommodationSecondaryImage || '';
    card.dataset.wetuBridgeTertiaryImage = imagePool.accommodationImages[2] || assignment.tertiary_image_url || '';
    card.dataset.wetuBridgePropertyImage = imagePool.propertyImages[0] || assignment.property_image_url || '';
    card.dataset.wetuBridgeGalleryCount = String(combinedGalleryImages.length || 0);
    card.dataset.wetuBridgeSummary = assignment.summary || assignment.property_editorial_summary || '';
    card.dataset.wetuBridgeVariantId = assignment.variant_id || '';
    card.dataset.wetuBridgeProductId = assignment.product_id || '';
    card.dataset.wetuBridgePropertyName = assignment.property_title || '';
    card.dataset.wetuBridgePropertyHandle = assignment.property_handle || '';
    card.dataset.wetuBridgePropertyUrl = assignment.property_url || '';
    card.dataset.wetuBridgeLocation = assignment.location_display || '';
    card.dataset.wetuBridgeRoomType = assignment.room_type || '';
    card.dataset.wetuBridgeRoomBasis = assignment.room_basis || '';
    card.dataset.wetuBridgeBeds = assignment.beds_summary || '';
    card.dataset.wetuBridgeReady = 'true';
  }

  function applyBridgeAssignments(wrapper, bridgeData) {
    const cards = Array.from(wrapper.querySelectorAll(CARD_SELECTOR));
    if (!cards.length) return;

    const signature = cards.map((card) => card.dataset.accommodationId || '').join('|');
    const state = wrapperState.get(wrapper) || {};
    const needsRefresh = cards.some((card) => !card.dataset.wetuBridgeReady);

    if (state.signature === signature && !needsRefresh) return;

    const allAccommodations = Array.isArray(bridgeData?.accommodations) ? bridgeData.accommodations : [];
    const fallbackPool = allAccommodations.filter((a) => hasText(a?.image_url));
    let fallbackIndex = 0;

    cards.forEach((card) => {
      const sourceId = normalizeSourceId(card.dataset.accommodationId || '');

      let assignment = sourceId ? globalAssignmentsBySourceId.get(sourceId) : null;

      if (!assignment) {
        const matchPayload = getAccommodationMatchPayload({
          source_id: sourceId,
          handle: card.dataset.accommodationHandle || card.dataset.handle,
          name: inferAccommodationNameFromCard(card),
          property_handle: card.dataset.wetuBridgePropertyHandle || card.dataset.propertyHandle,
          property_name: inferPropertyNameFromCard(card),
        });

        assignment = allAccommodations.find((candidate) => {
          if (matchPayload.sourceId && normalizeSourceId(candidate?._source_id || candidate?.source_id) === matchPayload.sourceId) {
            return true;
          }

          if (matchPayload.propertyAndNameKey && String(candidate?._property_and_name_key || '').trim() === matchPayload.propertyAndNameKey) {
            return true;
          }

          if (matchPayload.handleKey && normalizeMatchKey(candidate?._handle_key || candidate?.handle) === matchPayload.handleKey) {
            return true;
          }

          return false;
        }) || null;
      }

      if (!assignment && fallbackPool.length) {
        assignment = fallbackPool[fallbackIndex % fallbackPool.length];
        fallbackIndex += 1;
      }

      try {
        applyAssignmentToCard(card, assignment);
        enhanceCardPresentation(card);
      } catch (error) {
        // DOM timing error — card structure was modified by extension
      }

      if (!card.dataset.wetuBridgeReady) {
        card.dataset.wetuBridgeReady = 'true';
      }
    });

    wrapperState.set(wrapper, { signature });
  }

  function setupWrapper(wrapper, bridgeData) {
    if (initializedWrappers.has(wrapper)) return;
    initializedWrappers.add(wrapper);

    wrapper.classList.add('wetu-bridge-active');

    let rafToken = null;
    const scheduleApply = function () {
      if (rafToken !== null) return;

      rafToken = window.requestAnimationFrame(() => {
        rafToken = null;
        applyBridgeAssignments(wrapper, bridgeData);
      });
    };

    wrapper.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest(BUTTON_SELECTOR);
        if (!button) return;

        const card = button.closest(CARD_SELECTOR);
        if (!card) return;

        rememberCardAssignment(card);

        if (card.dataset.wetuBridgeVariantId) {
          button.dataset.variantId = card.dataset.wetuBridgeVariantId;
          card.dataset.variantId = card.dataset.wetuBridgeVariantId;
        }

        patchResultPayloadFromCard(card);
      },
      true
    );

    const observer = new MutationObserver((mutations) => {
      const hasStructuralChange = mutations.some((mutation) => {
        return mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
      });

      if (hasStructuralChange) scheduleApply();
    });

    observer.observe(wrapper, { childList: true, subtree: true });
    scheduleApply();
  }

  function startPrototypePatchLoop(bridgeData) {
    const maxAttempts = 120;
    let attempts = 0;

    const attemptPatch = function () {
      if (patchTravelSearchPrototype(bridgeData)) return;

      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(attemptPatch, 150);
      }
    };

    attemptPatch();
  }

  function boot() {
    const bridgeData = parseBridgeData();
    if (!bridgeData || !bridgeData.accommodations.length) return;

    installCartPayloadBridge();
    startPrototypePatchLoop(bridgeData);

    const bindWrappers = function (root) {
      if (!(root instanceof Element || root instanceof Document)) return;

      const wrappers = root.querySelectorAll(WRAPPER_SELECTOR);
      wrappers.forEach((wrapper) => setupWrapper(wrapper, bridgeData));
    };

    bindWrappers(document);

    const rootObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (node.matches(WRAPPER_SELECTOR)) {
            setupWrapper(node, bridgeData);
            continue;
          }

          if (node.querySelector(WRAPPER_SELECTOR)) {
            bindWrappers(node);
          }
        }
      }
    });

    rootObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
